import { container } from 'tsyringe';
import { BackendClient } from '../connectivity/backend-client';
import { isGimmicksBoard } from '../domain/gimmicks/gimmicks-board.helper';

/**
 * The concrete targets the settings page "Allgemein" offers under Linkview and Wissen (feature plan 0069).
 *
 * Raw query rows on purpose, not the repositories:
 * - `LINKVIEW_REPOSITORY.items$` starts as `[]` and swallows load errors, so "loading", "empty" and
 *   "failed" look the same there;
 * - `KnowledgeObjectRepository.findAllFiltered` puts every row into the entity cache through `fromDTO`,
 *   and its `items$` follows the sidebar's filters.
 *
 * No filter in the query: the server's soft-delete filter already leaves out the trash (QueryAction). One row
 * is dropped here, on the client: the Gimmicks board, a `linkview` row the server refuses as a Linkboard
 * target (feature plan 0071, StartPageTargets.php). Errors are not caught here; the page shows them.
 */

/** One selectable target: the ShortId, its title and, for knowledge objects, the kind. */
export interface StartPageTargetRow {
    readonly uid: string;
    readonly title: string;
    readonly type?: string;
}

const QUERIES: readonly { readonly name: string; readonly path: string }[] = [
    {name: 'linkview', path: '/api/v1/query/linkview'},
    {name: 'knowledge', path: '/api/v1/query/knowledge-object'},
];

/**
 * The targets of one area (`linkview` or `knowledge`), unsorted. An area without targets answers `[]`
 * without a request.
 */
export async function loadStartPageTargets(name: string): Promise<StartPageTargetRow[]> {
    const path = QUERIES.find(query => query.name === name)?.path;
    if (!path) {
        return [];
    }
    const rows = await container.resolve(BackendClient).post<StartPageTargetRow[]>(path, false, {queryParams: []});
    return (rows ?? [])
        .filter(row => !(name === 'linkview' && isGimmicksBoard(row.uid)))
        .map(row => ({uid: row.uid, title: row.title, type: row.type}));
}
